import { Controller } from "@hotwired/stimulus"

// The @-picker for the cockpit narrative box — Notion-style.
//
// Two things it does differently from the chat composer:
//
//  1. It offers all THREE levels a watch can point at — a whole STORY, one PAGE, or a single
//     COMPONENT — because they answer different questions ("anything in here" vs "just this
//     table").
//  2. It inserts "@Name" only. The chat composer writes "@Name [uuid]" straight into the text,
//     which is fine for a one-line question but unreadable in the middle of a paragraph the
//     user is writing. The id travels in a hidden field instead, keyed by the name.
export default class extends Controller {
  static targets = ["input", "menu", "submit", "mentions"]
  static values  = { searchUrl: String }

  connect() {
    this.open = false
    // Seeded by the server when editing saved watches, and when returning from Confirm.
    // Starting from [] made syncMentions prune every restored tag on the first keystroke — the
    // hidden field went back to "[]" and #save then rewrote every ask with no report.
    this.picked = this.seededMentions()    // [{name, type, id}] in the order they were chosen
    this.crumbs = []                       // breadcrumb returned by the server, for display
    this.stack  = []                       // parents we drilled through — the source of truth
                                           // for "back"; deriving it from the breadcrumb was
                                           // fragile and left ← doing nothing.
    this.activeIndex = 0
    this.close = this.close.bind(this)
    document.addEventListener("click", this.close)
    this.hide()                            // never render with the picker already open
    if (this.hasInputTarget) this.autogrow()   // a restored draft should show at full height
  }

  disconnect() {
    document.removeEventListener("click", this.close)
    clearTimeout(this.timer)
  }

  onInput() {
    this.autogrow()
    this.syncMentions()

    const term = this.currentTerm()
    if (term === null) return this.hide()

    // Open immediately on a bare "@" so the list is there at once; debounce only the filtering,
    // since the user is writing a sentence rather than hunting a picker.
    clearTimeout(this.timer)
    if (term === "") {
      // Back at a bare "@" — start again from the top of the tree.
      this.stack = []
      return this.search("")
    }
    this.timer = setTimeout(() => { this.stack = []; this.search(term) }, 200)
  }

  // Arrow keys + Enter, so the whole thing works without leaving the keyboard.
  onKeydown(event) {
    if (!this.open || !this.hasMenuTarget) return
    const items = this.menuTarget.querySelectorAll("[data-mention-item]")

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        if (!items.length) return
        event.preventDefault()
        const step = event.key === "ArrowDown" ? 1 : -1
        this.activeIndex = (this.activeIndex + step + items.length) % items.length
        this.highlight(items)
        return
      }
      case "ArrowRight": {
        // Drill into the highlighted row. Only when the caret is at the end of the term,
        // otherwise → is just moving through text the user is editing.
        const row = items[this.activeIndex]
        if (!row || row.dataset.hasChildren !== "true") return
        if (this.inputTarget.selectionStart !== this.inputTarget.value.length) return
        event.preventDefault()
        this.enter(row)
        return
      }
      case "ArrowLeft":
      case "Backspace": {
        if (!this.stack.length) return                // nothing to go back to
        if (this.currentTerm() !== "") return         // let it edit the typed text
        event.preventDefault()
        this.back()
        return
      }
      case "Enter":
      case "Tab": {
        if (!items.length) return
        event.preventDefault()
        items[this.activeIndex]?.click()
        return
      }
      case "Escape":
        event.preventDefault()
        this.hide()
    }
  }

  // Step INTO a row: remember where we were, then browse its children.
  enter(row) {
    this.stack.push({ type: row.dataset.type, id: row.dataset.id })
    this.search("", this.stack[this.stack.length - 1])
  }

  // Step back OUT: drop the level we're in and re-browse its parent (or the root).
  back() {
    this.stack.pop()
    this.search("", this.stack[this.stack.length - 1] || null)
  }

  highlight(items) {
    items.forEach((el, i) => el.classList.toggle("is-active", i === this.activeIndex))
    items[this.activeIndex]?.scrollIntoView({ block: "nearest" })
  }

  // The word being typed after an "@", or null if the caret isn't in one.
  currentTerm() {
    const el = this.inputTarget
    const at = el.value.lastIndexOf("@", el.selectionStart - 1)
    if (at === -1) return null

    const fragment = el.value.slice(at + 1, el.selectionStart)
    if (/[\n]/.test(fragment) || fragment.length > 40) return null
    this.atIndex = at
    return fragment
  }

  // Browse (no term) walks the tree one level at a time; typing switches to a flat search,
  // because someone who types a name wants it found, not navigated to.
  async search(term, parent = null) {
    const params = new URLSearchParams()
    if (term) params.set("q", term)
    if (parent) { params.set("parent_type", parent.type); params.set("parent_id", parent.id) }

    try {
      const response = await fetch(`${this.searchUrlValue}?${params}`,
                                   { headers: { Accept: "application/json" } })
      if (!response.ok) return this.hide()
      const data = await response.json()
      this.crumbs = data.breadcrumb || []
      this.render(data.items || [])
    } catch (_) {
      this.hide()
    }
  }

  render(hits) {
    const crumbs = this.crumbs.length
      ? `<div class="laz-compose-crumbs">${this.crumbs.map((c) =>
           `<span class="laz-compose-crumb">${this.escape(c.name)}</span>`).join(
           `<span class="laz-compose-crumb-sep">/</span>`)}<span class="laz-compose-crumb-sep">/</span>
         <span class="laz-compose-crumb-back" data-action="click->cockpit-compose#backClick">← back</span></div>`
      : ""

    if (!hits.length) {
      this.menuTarget.innerHTML =
        `${crumbs}<div class="laz-compose-menu-hint">Nothing here.</div>`
      this.menuTarget.hidden = false
      this.open = true
      return
    }

    this.activeIndex = 0
    this.menuTarget.innerHTML = crumbs + hits.map((h, i) => `
      <button type="button" class="laz-compose-menu-item${i === 0 ? " is-active" : ""}"
              data-mention-item
              data-type="${h.type}" data-id="${h.id}" data-name="${this.escape(h.name)}"
              data-has-children="${h.has_children}"
              data-action="click->cockpit-compose#pick">
        <span class="laz-compose-menu-level laz-compose-menu-level-${h.level.toLowerCase()}">${h.level}</span>
        <span class="laz-compose-menu-main">
          <span class="laz-compose-menu-name">${this.escape(h.name)}</span>
        </span>
        <span class="laz-compose-menu-sub">${this.escape(h.context)}</span>
        ${h.has_children
          ? `<span class="laz-compose-menu-into" title="Open (→)"
                   data-type="${h.type}" data-id="${h.id}" data-has-children="true"
                   data-action="click->cockpit-compose#drillInClick">›</span>`
          : `<span class="laz-compose-menu-into is-leaf"></span>`}
      </button>
    `).join("")
    this.menuTarget.hidden = false
    this.open = true
  }

  // The chevron drills in; clicking anywhere else on the row selects that level.
  drillInClick(event) {
    event.preventDefault()
    event.stopPropagation()
    this.enter(event.currentTarget)
  }

  // "← back" in the breadcrumb.
  backClick(event) {
    event.preventDefault()
    event.stopPropagation()
    this.back()
  }

  pick(event) {
    const b = event.currentTarget
    const el = this.inputTarget
    const name = b.dataset.name

    // Only the readable name goes into the text; the binding is remembered separately.
    const tag = `@${name} `
    el.value = el.value.slice(0, this.atIndex) + tag + el.value.slice(el.selectionStart)
    const caret = this.atIndex + tag.length
    el.focus()
    el.setSelectionRange(caret, caret)

    this.picked = this.picked.filter((m) => m.name !== name)
    this.picked.push({ name, type: b.dataset.type, id: b.dataset.id })
    this.syncMentions()
    this.autogrow()
    this.hide()
  }

  seededMentions() {
    if (!this.hasMentionsTarget) return []
    try {
      const list = JSON.parse(this.mentionsTarget.value || "[]")
      return Array.isArray(list) ? list.filter((m) => m && m.name && m.id) : []
    } catch {
      return []
    }
  }

  // Drop any mention whose "@Name" the user has since deleted or edited, so a stale binding
  // can't attach itself to text that no longer mentions it.
  syncMentions() {
    if (!this.hasMentionsTarget) return
    const text = this.inputTarget.value
    this.picked = this.picked.filter((m) => text.includes(`@${m.name}`))
    this.mentionsTarget.value = JSON.stringify(this.picked)
  }

  hide() {
    if (!this.hasMenuTarget) return
    this.menuTarget.hidden = true
    this.open = false
  }

  close(event) {
    if (!this.element.contains(event.target)) this.hide()
  }

  // Turbo is off on this form (the next step renders rather than redirects), so the browser does
  // a full navigation and the only native feedback is the tab spinner. Say what's happening on
  // the button, and block a second click — a double submit would run the split twice.
  submitting(event) {
    if (this.inputTarget.value.trim() === "") return
    if (this.submitted) { event.preventDefault(); return }
    this.submitted = true

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.innerHTML =
        `<span class="laz-cx-spin" aria-hidden="true"></span>Reading what you wrote…`
    }
  }

  useChip(event) {
    const el = this.inputTarget
    const text = event.currentTarget.textContent.trim()
    el.value = el.value.trim() ? `${el.value.trim()}\n${text}` : text
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
    this.autogrow()
  }

  autogrow() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`
  }

  escape(text) {
    const d = document.createElement("div")
    d.textContent = text ?? ""
    return d.innerHTML
  }
}
